import React from 'react';
import { AlertTriangle } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'shadcn/components/ui/tooltip';
import { cn } from 'shadcn/lib/utils';

import {
    getCompanySourceBadgeLabel,
    getCompanySourceColors,
} from '../AllocationBreakdown/companySourceAllocationConfig';

/** @param {{ message: string }} props */
function VarianceIcon({ message }) {
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span className="inline-flex items-center" aria-label={message} role="img">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-700" aria-hidden="true" />
                </span>
            </TooltipTrigger>
            <TooltipContent className="z-[10000]" side="top">
                {message}
            </TooltipContent>
        </Tooltip>
    );
}

/**
 * @param {Object} props
 * @param {string} [props.className]
 * @param {number} props.grandTotal
 * @param {Array<{ companySource: { id: number, code: string, displayName: string }, numCases: number }>} props.bySource
 * @param {boolean} [props.isThirdPartyAsn]
 * @param {{ sourceWarnings: Map<number, string>, globalMessages: string[] }} [props.varianceWarnings]
 */
function ReceiveFooterSummary({
    bySource,
    className,
    grandTotal,
    isThirdPartyAsn = false,
    varianceWarnings = { sourceWarnings: new Map(), globalMessages: [] },
}) {
    const globalMessage = varianceWarnings.globalMessages.join('; ') || null;

    return (
        <div
            className={cn(
                'flex flex-1 items-center justify-between gap-4 rounded-lg border bg-muted/30 px-4 py-3',
                className,
            )}
        >
            <p className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
                Grand Total Cases: {grandTotal}
                {globalMessage ? <VarianceIcon message={globalMessage} /> : null}
            </p>
            {!isThirdPartyAsn ? (
                <div className="flex flex-wrap items-center justify-end gap-2">
                    {bySource.map(({ companySource, numCases }) => {
                        const badgeLabel = getCompanySourceBadgeLabel(companySource.code);
                        const label = badgeLabel ?? companySource.displayName;
                        const { badgeColor } = getCompanySourceColors(companySource.code);
                        const sourceWarning = varianceWarnings.sourceWarnings.get(companySource.id);

                        return (
                            <span
                                key={companySource.id}
                                className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium"
                                style={{ borderColor: badgeColor, color: badgeColor }}
                            >
                                {label}: {numCases}
                                {sourceWarning ? <VarianceIcon message={sourceWarning} /> : null}
                            </span>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}

export { ReceiveFooterSummary };
